// What a JSON document holds. Thing data, tool and router arguments, and
// answer data are JSON, so they are typed as JSON rather than as a bag of
// unknown that a caller casts later.

import { Schema } from "effect";

/**
 * Decodes a value read from outside, such as a vendor's response or a value
 * that crossed RPC, into Json. It walks the whole value.
 */
export const Json = Schema.MutableJson;
/**
 * A JSON value. Thing data and assertion data are typed as Json, so read a
 * field by narrowing it (`typeof v === "string"`) or by decoding it with a
 * Schema, never by a cast.
 */
export type Json = typeof Json.Type;

/**
 * Decodes a value read from outside, such as a vendor's response or a value
 * that crossed RPC, into a JsonObject. It walks the whole value.
 */
export const JsonObject = Schema.Record(Schema.String, Schema.mutableKey(Json));
/** A JSON object, such as a Thing's `data` or an assertion's data. */
export type JsonObject = typeof JsonObject.Type;

const isParsedJsonObject = (u: unknown): u is JsonObject => typeof u === "object" && u !== null && !Array.isArray(u);
/**
 * A JSON object that JSON.parse produced, such as Thing data from WarmHub's
 * response or the lookup cache in KV: JSON.parse can return only JSON, so
 * only the top level is checked, and a large geometry isn't walked again.
 * Don't use it for a value that didn't come from JSON.parse; use JsonObject.
 * Verified by: test/warmhub-live.test.ts, "reads Thing data that isn't a JSON
 * object as a failed read, without walking its values".
 */
export const ParsedJsonObject = Schema.declare(isParsedJsonObject);
/**
 * Parses JSON text. JSON.parse without a reviver returns only JSON values,
 * so its result is Json, not the `any` the standard library declares. It
 * throws a SyntaxError on text that isn't JSON. Decode the result with a
 * Schema before reading it as a narrower type.
 */
export const parseJson: (text: string) => Json = JSON.parse;

/**
 * Whether a Json value is a JSON object, not an array, null or a scalar. It
 * tests only the value's own kind: `j` is already Json, so its members are.
 */
export function isJsonObject(j: Json): j is JsonObject {
  return typeof j === "object" && j !== null && !Array.isArray(j);
}
